'use client';

import Link from 'next/link';

const ALLOWED_LINKS = [
  { label: 'Privacy Policy', href: '/policies/privacy-policy' },
  {
    label: 'Returns & Refunds Policy',
    href: '/policies/returns-refunds-policy',
  },
  { label: 'Sitemap', href: '/sitemap' },
  { label: 'Terms & Conditions', href: '/policies/terms-services' },
  { label: 'FAQs', href: '/faqs' },
];

export default function MaintenancePage() {
  return (
    <main className='relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#faf7f5] px-4 py-16 text-center'>
      {/* Decorative blobs */}
      <div
        aria-hidden='true'
        className='pointer-events-none absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-[#a0383f]/8 blur-3xl'
      />
      <div
        aria-hidden='true'
        className='pointer-events-none absolute -bottom-32 -right-32 h-[400px] w-[400px] rounded-full bg-[#d48a7a]/10 blur-3xl'
      />

      {/* Card */}
      <div className='relative z-10 w-full max-w-lg rounded-2xl border border-[#edeae9] bg-white px-8 py-12 shadow-[0_8px_48px_rgba(160,56,63,0.08)] md:px-12'>
        {/* Icon */}
        <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-pale-rose'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-8 w-8 text-fig'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={1.5}>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z'
            />
          </svg>
        </div>

        {/* Status pill */}
        <div className='mb-5 inline-flex items-center gap-2 rounded-full border border-fig/20 bg-pale-rose px-4 py-1.5'>
          <span className='h-2 w-2 animate-pulse rounded-full bg-fig' />
          <span className='text-xs font-semibold uppercase tracking-widest text-fig'>
            Scheduled Maintenance
          </span>
        </div>

        <h1 className='mb-3 text-2xl font-bold leading-tight text-[#38383a] md:text-3xl'>
          A brief pause
          <br />
          to refine your experience
        </h1>

        <p className='mx-auto mb-8 max-w-sm text-base leading-relaxed text-[#6b7280]'>
          Thoughtful updates are underway to elevate your experience. We'll be
          back shortly!
        </p>

        {/* Progress bar */}
        <div className='mx-auto mb-8 h-1 w-full max-w-xs overflow-hidden rounded-full bg-dew'>
          <div className='h-full w-2/5 animate-[indeterminate_2.4s_ease-in-out_infinite] rounded-full bg-fig' />
        </div>

        {/* Divider */}
        <div className='mb-6 h-px bg-dew' />

        {/* Contact */}
        <p className='mb-6 text-sm text-[#9ca3af]'>
          Need help?{' '}
          <a
            href='mailto:care@foodstories.shop'
            className='font-medium text-fig underline-offset-2 hover:underline'>
            care@foodstories.shop
          </a>{' '}
          &nbsp;·&nbsp;{' '}
          <a
            href='tel:+919004171401'
            className='font-medium text-fig underline-offset-2 hover:underline'>
            +91 900 417 1401
          </a>
        </p>

        {/* Public links */}
        <div className='flex flex-wrap justify-center gap-3'>
          {ALLOWED_LINKS.map(({ label, href }) => (
            <Link
              key={href}
              href={href}
              className='rounded-full border border-dew bg-[#faf7f5] px-4 py-1.5 text-xs font-medium text-[#6b7280] transition-colors hover:border-fig/30 hover:bg-pale-rose hover:text-fig'>
              {label}
            </Link>
          ))}
        </div>
      </div>

      {/* Footer note */}
      <p className='mt-8 text-xs text-[#d1cdc9]'>
        © {new Date().getFullYear()} Foodstories. All rights reserved.
      </p>

      <style jsx>{`
        @keyframes indeterminate {
          0% {
            transform: translateX(-250%);
          }
          100% {
            transform: translateX(600%);
          }
        }
      `}</style>
    </main>
  );
}
